import { datadogLogs } from "@datadog/browser-logs";
import { RedoClient } from "@redotech/redo-api-client";
import { contactDataFormat } from "@redotech/redo-model/customer-contact-definition";
import {
  CART_ATTRIBUTE_KEY,
  ExtendedWarrantyCartToggleExperience,
  ExtendedWarrantyPdpRenderMethod,
  ExtendedWarrantyPopupExperience,
} from "@redotech/redo-model/extended-warranty-enable-criteria";
import {
  AttachmentStrategy,
  isAttachmentStrategyUsingCartToggles,
} from "@redotech/redo-model/returns/attachment-strategy";
import {
  ANONYMOUS_ID_CART_ATTRIBUTE_KEY,
  ANONYMOUS_SHOPIFY_SESSION_ID_CART_ATTRIBUTE_KEY,
} from "@redotech/redo-model/shopify/cart";
import { Treatment } from "@redotech/redo-model/split";
import {
  applyTreatment,
  pickTreatment,
} from "@redotech/redo-storefront-common/config/handle-split-testing";
import { AllCookies, getCookie } from "@redotech/redo-web/utils/cookies";
import { setLocalStorageWithExpiry } from "@redotech/redo-web/utils/local-storage-wrapper";
import {
  getSessionStorageWithExpiry,
  setSessionStorageWithExpiry,
} from "@redotech/redo-web/utils/session-storage-wrapper";
import {
  ANONYMOUS_SHOPIFY_SESSION_ID_KEY,
  ANONYMOUS_SHOPPER_ID_KEY,
  COVERAGE_TREATMENT_ID_QUERY_PARAM,
  CUSTOMER_EMAIL,
  CUSTOMER_PHONE,
} from "@redotech/redo-web/utils/shared-conf";
import {
  ShopifyAjaxClient,
  ShopifyProducts,
} from "@redotech/shopify-client/ajax";
import { storeTransform } from "@redotech/util/store";
import { sessionStore } from "@redotech/web-util/storage";
import { getAutocheckCache } from "./autocheck-cache";
import { createExtensionShopifyClient } from "./client/client-factory";
import {
  ShopifyExtensionClient,
  trackUserSession,
} from "./client/shopify-extension-client";
import { StorefrontClient } from "./client/storefront";
import { CONCIERGE_PRODUCT_FORM_CONTAINER_ID } from "./concierge/pre-init";
import { getInternalAppStatusEmitter, getRedoStorefrontConfig } from "./config";
import { getFeesCache } from "./fees-cache";
import { isMarketingEmbeddedFormEnabled } from "./marketing-embedded-form/pre-init";
import { log, mountRedoDebugFunction } from "./monitoring/log";
import { executeAndMonitor, initEmbedMonitor } from "./monitoring/monitor";
import { initializeTiming, reportTiming } from "./monitoring/timing";
import { checkForPlacementTestMode } from "./placement-test/token-utils";
import {
  initializeIfReviewsElementsFound,
  isReviewsEnabled,
} from "./reviews/pre-init";
import { isOnProductPage } from "./shopify";
import { isTryFirstSetup } from "./try-first/pre-init";
import { getLocalStorageIgnoreExpiry, getStorefrontAccessToken } from "./utils";
import {
  REDO_CART_TOKEN_STORAGE_KEY,
  setInitialCartAttributes,
} from "./utils/cart";
import { getRedoProductsCache } from "./utils/redo-products-cache";

if (location.href.includes("/completeExchange")) {
  const url = new URL(location.href);
  url.pathname = "/apps/redo" + url.pathname;
  location.href = url.toString();
}

// FIXME Fix types for globalThis
if ((globalThis as any).redo_app_loaded_on_page) {
  console.log("Redo already loaded, skipping re-initialization");
  // Don't throw error - just skip re-initialization (e.g., page refresh in placement test mode)
} else {
  (globalThis as any).redo_app_loaded_on_page = true;
}

if (!globalThis.redoWidgetId) {
  const scriptUrl = new URL(
    (document.currentScript as HTMLScriptElement).src,
    location.href,
  );

  const params = new URLSearchParams(new URL(scriptUrl, location.href).search);

  globalThis.redoWidgetId = globalThis.redoWidgetId ?? params.get("widget_id");
}

// Initialize Redo storefront configuration
getRedoStorefrontConfig();

// Initialize internal app status emitter
getInternalAppStatusEmitter();

const exchangeSelectStore = storeTransform(
  sessionStore("redo.exchange-select"),
  {
    read: (string) => string !== undefined,
    write: (value) => (value ? "" : undefined),
  },
);

if (!localStorage.getItem(ANONYMOUS_SHOPPER_ID_KEY)) {
  localStorage.setItem(ANONYMOUS_SHOPPER_ID_KEY, crypto.randomUUID());
}

// Initialize timing tracking
initializeTiming();

// Check for placement test mode FIRST, before any other initialization
void executeAndMonitor(async () => {
  const testConfig = checkForPlacementTestMode();

  if (testConfig) {
    console.log("Placement test mode detected, initializing configurator...");

    // Block normal widget initialization when in test mode
    // MUST be set BEFORE async import to prevent race condition with widget initialization
    globalThis.blockRedo = true;

    // Dynamically import the initializer to keep main bundle small
    const { initializePlacementConfigurator } = await import(
      "./placement-test/initializer"
    );

    initializePlacementConfigurator(testConfig);
    return;
  }
});

void executeAndMonitor(async () => {
  if (globalThis.blockRedo) {
    return;
  }
  const autocheck = await getAutocheckCache();

  try {
    initEmbedMonitor({ autocheck });
  } catch (error) {
    console.error("Error initializing autocheck:", error);
  }
});

// Extended Warranty
void executeAndMonitor(async () => {
  if (globalThis.blockRedo) {
    return;
  }
  if (globalThis.redoStorefront?.type === "commentsold") {
    return;
  }

  const autocheck = await getAutocheckCache();

  if (autocheck?.extendedWarranties?.enabled) {
    log(
      "Extended warranties enabled. Will get client and check display settings.",
    );
    const ajaxClient = ShopifyAjaxClient.fromEnv();
    try {
      const cart = await ajaxClient.cartGet();
      // Initialize the cart attribute if it doesn't exist
      if (!cart?.attributes?.[CART_ATTRIBUTE_KEY]) {
        await ajaxClient.updateCartAttribute({
          attributes: [{ key: CART_ATTRIBUTE_KEY, value: JSON.stringify({}) }],
        });
      }
    } catch (error) {
      log("Error initializing cart attribute", error);
    }

    const extensionShopifyClient = new StorefrontClient(
      globalThis.Shopify?.shop ??
        globalThis.Shopify?.Checkout?.apiHost ??
        globalThis.redoStorefront?.storeUrl ??
        "",
      getStorefrontAccessToken(autocheck) ?? "",
      globalThis?.Shopify?.Checkout?.cartToken ||
        (await ajaxClient.cartGet()).token!,
    );

    // TODO: When these are added to ab testing, move inside the EwSettingsProvider
    const cartToggleExperience =
      autocheck?.extendedWarranties?.cartToggleExperience;
    if (
      cartToggleExperience &&
      cartToggleExperience !== ExtendedWarrantyCartToggleExperience.DISABLED
    ) {
      log(
        "Extended warranties cart toggle experience is configured. Will initialize cart toggle.",
      );
      try {
        const { initialize: initializeExtendedWarranty } = await import(
          "./extended-warranty/cart-toggle/initializer"
        );
        initializeExtendedWarranty(extensionShopifyClient);
      } catch (error: any) {
        console.error("Error initializing extended warranty", error);
      }
    }
    // TODO: When these are added to ab testing, move inside the EwSettingsProvider
    const pdpRenderMethod = autocheck?.extendedWarranties?.pdpRenderMethod;
    if (pdpRenderMethod === ExtendedWarrantyPdpRenderMethod.SCRIPT_TAG) {
      log(
        "Extended warranties PDP render method is configured. Will initialize PDP options.",
      );
      try {
        const { initialize: initializeExtendedWarranty } = await import(
          "./extended-warranty/pdp-options/initializer"
        );
        initializeExtendedWarranty(extensionShopifyClient);
      } catch (error: any) {
        console.error("Error initializing extended warranty", error);
      }
    }
    const popupExperience = autocheck?.extendedWarranties?.popupExperience;
    if (
      popupExperience &&
      popupExperience !== ExtendedWarrantyPopupExperience.DISABLED
    ) {
      log(
        "Extended warranties popup experience is configured. Will initialize popup toggle.",
      );
      try {
        const { initialize: initializeExtendedWarranty } = await import(
          "./extended-warranty/popup-toggle/initializer"
        );
        initializeExtendedWarranty(extensionShopifyClient);
      } catch (error: any) {
        console.error("Error initializing extended warranty", error);
      }
    }
  }
});

// Delivery estimate
void executeAndMonitor(async () => {
  if (globalThis.blockRedo) {
    return;
  }
  if (globalThis.redoStorefront?.type === "commentsold") {
    return;
  }
  const autocheck = await getAutocheckCache();

  if (autocheck?.showPDPDeliveryEstimate) {
    try {
      const { initialize: initializeDeliveryEstimate } = await import(
        "./delivery-estimate"
      );
      initializeDeliveryEstimate(autocheck);
    } catch (error: any) {
      console.error("Error initializing delivery estimate:", error);
    }
  }
});

// Marketing embedded form
void executeAndMonitor(async () => {
  if (globalThis.blockRedo) {
    return;
  }
  const autocheck = await getAutocheckCache();

  if (!isMarketingEmbeddedFormEnabled({ autocheck })) {
    return;
  }

  log("Initializing marketing embedded form");

  try {
    const { initialize: initializeMarketingEmbeddedForm } = await import(
      "./marketing-embedded-form/marketing-embedded-form"
    );
    initializeMarketingEmbeddedForm(autocheck);
  } catch (error: any) {
    console.error("Error initializing marketing embedded form:", error);
  }
});

// Review stars
void executeAndMonitor(async () => {
  if (globalThis.blockRedo) {
    return;
  }
  if (globalThis.redoStorefront?.type === "commentsold") {
    return;
  }
  const autocheck = await getAutocheckCache();
  if (!isReviewsEnabled(autocheck)) {
    return;
  }

  log("Initializing reviews stars");

  try {
    await initializeIfReviewsElementsFound(autocheck); // Use dynamic imports inside the function to keep bundle small even though statically imported here.
  } catch (error: any) {
    console.error("Error initializing review stars:", error);
  }
});

// Try first
void executeAndMonitor(async () => {
  if (globalThis.blockRedo) {
    return;
  }
  if (globalThis.redoStorefront?.type === "commentsold") {
    return;
  }
  const autocheck = await getAutocheckCache();
  reportTiming("tryFirst.autocheckCacheRetrieved");

  try {
    if (!isTryFirstSetup(autocheck)) {
      return;
    }

    const { initialize: initializeTryFirst } = await import(
      "./try-first/initializer"
    );
    reportTiming("tryFirst.initializeFunctionCalled");

    const ajaxClient = ShopifyAjaxClient.fromEnv();
    const cart = await ajaxClient.cartGet();
    const cartToken = globalThis?.Shopify?.Checkout?.cartToken || cart.token;
    const shop =
      globalThis.Shopify?.shop ??
      globalThis.Shopify?.Checkout?.apiHost ??
      globalThis.redoStorefront?.storeUrl;

    if (!shop || !cartToken) {
      return;
    }

    const storefrontClient = new StorefrontClient(
      shop,
      getStorefrontAccessToken(autocheck),
      cartToken,
    );
    initializeTryFirst(autocheck, storefrontClient);
  } catch (error: any) {
    console.error("Error initializing try first:", error);
  }
});

// Cart optimization
void executeAndMonitor(async () => {
  if (globalThis.blockRedo) {
    return;
  }
  if (globalThis.redoStorefront?.type === "commentsold") {
    return;
  }
  const autocheck = await getAutocheckCache();

  try {
    const {
      isCartOptimizationEnabled,
      initialize: initializeCartOptimization,
    } = await import("./cart-optimization/initializer");

    if (!isCartOptimizationEnabled(autocheck)) {
      return;
    }

    const ajaxClient = ShopifyAjaxClient.fromEnv();
    const cart = await ajaxClient.cartGet();
    const cartToken = globalThis?.Shopify?.Checkout?.cartToken || cart.token;
    const shop =
      globalThis.Shopify?.shop ?? globalThis.Shopify?.Checkout?.apiHost;

    if (!shop || !cartToken) {
      return;
    }

    const storefrontClient = new StorefrontClient(
      shop,
      getStorefrontAccessToken(autocheck),
      cartToken,
    );
    initializeCartOptimization(autocheck, storefrontClient);
  } catch (error: any) {
    console.error("Error initializing cart optimization:", error);
  }
});

// Loyalty page customer view
void executeAndMonitor(async () => {
  if (globalThis.blockRedo) {
    return;
  }
  if (globalThis.redoStorefront?.type === "commentsold") {
    return;
  }
  const autocheck = await getAutocheckCache();

  try {
    const { initialize: initializeLoyaltyPageCustomerView } = await import(
      "./loyalty-page/loyalty-page-customer-view"
    );
    initializeLoyaltyPageCustomerView(autocheck);
  } catch (error: any) {
    console.error("Error initializing loyalty page customer view:", error);
  }
});

// Redo storefront
void executeAndMonitor(async () => {
  if (globalThis.blockRedo) {
    return;
  }
  if (globalThis.redoStorefront?.type === "commentsold") {
    return;
  }

  const autocheck = await getAutocheckCache();

  const shouldRender =
    autocheck.marketing.enabled ||
    (autocheck.customerAccounts.enabled &&
      autocheck.customerAccounts.saveForLaterPanel?.enabled) ||
    autocheck.loyalty.enabled ||
    autocheck.reviews.enabled ||
    autocheck.conversion.enabled;

  if (!shouldRender) {
    return;
  }

  const ajaxClient = ShopifyAjaxClient.fromEnv();
  const cart = await ajaxClient.cartGet();
  const cartToken = globalThis?.Shopify?.Checkout?.cartToken || cart.token;
  const shop =
    globalThis.Shopify?.shop ??
    globalThis.Shopify?.Checkout?.apiHost ??
    globalThis.redoStorefront?.storeUrl;

  if (!shop || !cartToken) {
    console.error("Shop or cart token not found");
    return;
  }

  const storefrontClient = new StorefrontClient(
    shop,
    getStorefrontAccessToken(autocheck),
    cartToken,
  );

  try {
    const { initialize } = await import("./redo-storefront/index");
    initialize({
      autocheck,
      storefrontExtensionShopifyClient: storefrontClient,
    });
  } catch (error: any) {
    console.error("Error initializing redo storefront", error);
    (globalThis as any).redo_app_loaded_on_page = false;
  }
});

// Marketing popup form
void executeAndMonitor(async () => {
  if (globalThis.blockRedo) {
    return;
  }

  const autocheck = await getAutocheckCache();

  const queryParameters = new URLSearchParams(location.search);
  const testForm = queryParameters.get("redo-signup-test");
  const shouldRender = autocheck.marketing.enabled || testForm;

  if (!shouldRender) {
    return;
  }

  try {
    const { initialize } = await import(
      "./marketing-popup-form/marketing-popup-form"
    );
    initialize(autocheck);
  } catch (error: any) {
    console.error("Error initializing marketing popup form", error);
  }
});

// Conversion progress bar
void executeAndMonitor(async () => {
  if (globalThis.blockRedo) {
    return;
  }
  if (globalThis.redoStorefront?.type === "commentsold") {
    return;
  }

  const autocheck = await getAutocheckCache();

  if (
    !autocheck.conversion.enabled ||
    !autocheck.conversion.splitStorefrontFeatures?.progressBar
  ) {
    return;
  }

  const ajaxClient = ShopifyAjaxClient.fromEnv();
  const cart = await ajaxClient.cartGet();
  const cartToken = globalThis?.Shopify?.Checkout?.cartToken || cart.token;
  const shop =
    globalThis.Shopify?.shop ??
    globalThis.Shopify?.Checkout?.apiHost ??
    globalThis.redoStorefront?.storeUrl;

  if (!shop || !cartToken) {
    console.error("Shop or cart token not found for progress bar");
    return;
  }

  const storefrontClient = new StorefrontClient(
    shop,
    getStorefrontAccessToken(autocheck),
    cartToken,
  );

  try {
    const { initialize } = await import(
      "./redo-storefront/conversion/progress-bar/progress-bar-initializer"
    );
    initialize({
      progressBarConfig:
        autocheck.conversion.splitStorefrontFeatures.progressBar,
      storefrontClient,
      autocheck,
    });
  } catch (error: any) {
    console.error("Error initializing progress bars:", error);
  }
});

// Customer widget
void executeAndMonitor(async () => {
  if (globalThis.blockRedo) {
    return;
  }
  if (globalThis.redoStorefront?.type === "commentsold") {
    return;
  }

  const onTapcart = !!globalThis.Tapcart && !globalThis.Shopify;

  const redoClient = new RedoClient(
    globalThis.redoStorefront?.env?.REDO_API_URL ?? process.env.REDO_API_URL!,
  );
  const widgetClient = new ShopifyExtensionClient(
    redoClient,
    globalThis.redoWidgetId,
    globalThis.Shopify?.shop ??
      globalThis.Shopify?.Checkout?.apiHost ??
      globalThis.redoStorefront?.storeUrl,
  );

  const autocheck = await getAutocheckCache();

  if (autocheck?.customerWidgetEnabled) {
    log("Adding customer widget script");
    const storeUrl =
      globalThis.Shopify?.shop ??
      globalThis.Shopify?.Checkout?.apiHost ??
      globalThis.redoStorefront?.storeUrl;
    if (!globalThis.redoWidgetId && !storeUrl) {
      console.error("Widget ID or storeUrl required");
      return;
    }

    const CHAT_WIDGET_BASE =
      process.env.REDO_CHAT_WIDGET_URL || "https://chat-widget.getredo.com";

    const script = document.createElement("script");
    script.src = globalThis.redoWidgetId
      ? `${CHAT_WIDGET_BASE}/widget.js?widgetId=${globalThis.redoWidgetId}`
      : `${CHAT_WIDGET_BASE}/widget.js?storeUrl=${storeUrl}`;
    script.async = true;
    document.head.append(script);

    if (
      autocheck.concierge.placeConciergeProductForm &&
      (document.querySelector(CONCIERGE_PRODUCT_FORM_CONTAINER_ID) ||
        isOnProductPage(onTapcart))
    ) {
      const { addConciergeProductForm } = await import("./product-page");
      addConciergeProductForm(autocheck, onTapcart);

      const { initialize } = await import("./concierge/initializer");
      initialize({
        fromThemeExtension: true,
        autocheckSettings: autocheck.concierge.conciergeProductFormSettings,
      });
    }

    // concierge enabled when support.settings.ai.enabled is true
    if (autocheck.customerAccounts.enabled || autocheck.concierge.enabled) {
      try {
        await trackUserSession(widgetClient);
      } catch (error: any) {
        console.error("Error sending user session", error);
      }
    }
  }
});

// Exchange select
void executeAndMonitor(async () => {
  if (globalThis.redoStorefront?.type === "commentsold") {
    return;
  }
  const onTapcart = !!globalThis.Tapcart && !globalThis.Shopify;
  reportTiming("coverage.beforeAutocheckCacheRetrieved");
  const autocheck = await getAutocheckCache();
  reportTiming("coverage.autocheckCacheRetrieved");
  if (
    globalThis.redoStorefront?.type === "commercecloud" ||
    globalThis.redoStorefront?.type === "bigcommerce"
  ) {
    const shouldStartExchange = location.search.includes("redoExchangeSelect");
    if (shouldStartExchange) {
      exchangeSelectStore.set(true);
    }
  }
  if (exchangeSelectStore.get()) {
    const { initialize } = await import("./exchange-select");
    await initialize(autocheck, onTapcart);
    return;
  }

  if (globalThis.blockRedo) {
    return;
  }

  const queryString = location.search;
  const urlParams = new URLSearchParams(queryString);
  const urlID = urlParams.get(COVERAGE_TREATMENT_ID_QUERY_PARAM);
  const onPreview =
    urlParams.get("preview") === "true" ||
    urlParams.get("redo.mode") === "preview" ||
    getLocalStorageIgnoreExpiry("redo_preview");
  setLocalStorageWithExpiry("redo_preview", onPreview, 0.05);

  // Set global preview mode flag to bypass restrictions
  if (onPreview) {
    (globalThis as any).redoMode = "preview";
  }
  const localStorageTreatmentId =
    getLocalStorageIgnoreExpiry("redo_treatment_id");

  let selectedTreatment: Treatment | undefined;
  if (autocheck.treatments) {
    selectedTreatment = pickTreatment(
      autocheck.treatments,
      localStorageTreatmentId,
      urlID,
    );
  }
  reportTiming("coverage.beforeFeesCacheRetrieved");
  const fees = await getFeesCache();
  if ("error" in fees) {
    console.error("error in fees endpoint", { error: fees.error });
    return;
  }
  reportTiming("coverage.afterFeesCacheRetrieved");
  const storefrontClient = await createExtensionShopifyClient(
    autocheck,
    onTapcart,
  );

  // Set up debug logging for the shopify and checkout extensions
  mountRedoDebugFunction(storefrontClient);

  reportTiming("coverage.beforeCartCalls");

  if (selectedTreatment) {
    const treatmentId = applyTreatment({
      treatment: selectedTreatment,
      autocheck,
      fees,
    });
    setLocalStorageWithExpiry("redo_treatment_id", treatmentId, 0.5);
  }

  const storageCartToken = getSessionStorageWithExpiry(
    REDO_CART_TOKEN_STORAGE_KEY,
  );
  let cartToken: string | undefined =
    storageCartToken || globalThis?.Shopify?.Checkout?.cartToken;

  if (globalThis.redoStorefront?.type !== "commercecloud") {
    if (!cartToken) {
      log("cart token cache missed!");
      const cart = await setInitialCartAttributes(
        storefrontClient,
        selectedTreatment,
        autocheck,
      );
      cartToken = cart.token;
    } else {
      // don't await to save time
      log("cart token cache Hit!", cartToken);
      void setInitialCartAttributes(
        storefrontClient,
        selectedTreatment,
        autocheck,
      );
    }
  }

  const shop =
    globalThis.Shopify?.shop ??
    globalThis.Shopify?.Checkout?.apiHost ??
    globalThis.redoStorefront?.storeUrl;
  let shopifyStorefrontGraphqlClient: StorefrontClient | undefined = undefined;
  let storefrontTestPromise;
  if (
    !onTapcart &&
    cartToken &&
    shop &&
    getStorefrontAccessToken(autocheck) &&
    cartToken.includes("?")
  ) {
    shopifyStorefrontGraphqlClient = new StorefrontClient(
      shop,
      getStorefrontAccessToken(autocheck),
      cartToken,
    );
    storefrontTestPromise = shopifyStorefrontGraphqlClient.cartGet();
  }
  reportTiming("coverage.afterCartCalls");
  if (onTapcart) {
    // In the tapcart integration the cart is the best place to show our toggle so we override that setting
    autocheck.attachmentStrategy = AttachmentStrategy.SINGLE_TOGGLE;
  }

  // In preview mode, bypass certain restrictions
  const isPreviewMode = (globalThis as any).redoMode === "preview";

  if (
    !isPreviewMode &&
    (autocheck.disableRedoForCustomer ||
      !autocheck.enabled ||
      !fees ||
      !fees.handle ||
      (!autocheck.coverageEnabled &&
        !autocheck.finalSaleReturns?.coverageEnabled &&
        !autocheck.packageProtection?.coverageEnabled))
  ) {
    return;
  }
  reportTiming("coverage.beforeGettingVariants");
  const shopifyProducts: ShopifyProducts = await getRedoProductsCache({
    autocheck,
    fees,
    storefrontClient,
  });

  reportTiming("coverage.afterGettingVariants");
  let canUseStorefrontAPI = false;
  if (
    shopifyStorefrontGraphqlClient &&
    storefrontTestPromise &&
    !autocheck.shouldNotUseStorefrontApi
  ) {
    try {
      const storageVal = getSessionStorageWithExpiry(
        "redo_can_use_storefront_api",
      );

      if (storageVal !== undefined && storageVal !== null) {
        canUseStorefrontAPI = storageVal;
      } else {
        canUseStorefrontAPI = !!(await storefrontTestPromise);
        setSessionStorageWithExpiry(
          "redo_can_use_storefront_api",
          canUseStorefrontAPI,
        );
      }
    } catch {
      datadogLogs.logger.warn("storefront client failed", {
        team: autocheck.teamId,
        cartToken,
        platform: globalThis.redoStorefront?.type,
      });
      log("storefront client failed");
    }
  }

  log(`Can use storefront api? ${canUseStorefrontAPI}`);

  const cartEnabled =
    autocheck.cartToggle?.enabled ||
    isAttachmentStrategyUsingCartToggles[autocheck.attachmentStrategy] ||
    location.search.includes("redo-cart=true") ||
    sessionStorage.getItem("redo-cart") === "true" ||
    onTapcart; // Tapcart always has cart enabled

  reportTiming("coverage.beforeInitialize");
  const { initialize } = await import("./manager");
  initialize(
    storefrontClient,
    shopifyStorefrontGraphqlClient,
    autocheck,
    cartEnabled,
    fees,
    shopifyProducts,
    canUseStorefrontAPI,
  );
  reportTiming("coverage.initializeFunctionCalled");
});

async function ensureMarketingAnonymousIdPresent() {
  const autocheck = await getAutocheckCache();

  const marketingEnabled = autocheck.marketing.enabled;
  const conversionEnabled =
    autocheck.conversion.enabled &&
    autocheck.conversion.predictiveConversion.enabled;
  const customerAccountsEnabled = autocheck.customerAccounts.enabled;

  if (marketingEnabled || conversionEnabled || customerAccountsEnabled) {
    const existingAnonymousShopperId = localStorage.getItem(
      ANONYMOUS_SHOPPER_ID_KEY,
    );
    const idToUse = existingAnonymousShopperId ?? crypto.randomUUID();
    if (!existingAnonymousShopperId) {
      localStorage.setItem(ANONYMOUS_SHOPPER_ID_KEY, idToUse);
    }

    const existingShopifySessionId = localStorage.getItem(
      ANONYMOUS_SHOPIFY_SESSION_ID_KEY,
    );
    const shopifySessionIdToUse =
      getCookie(AllCookies.ShopifySessionId) ?? existingAnonymousShopperId;
    if (
      shopifySessionIdToUse &&
      shopifySessionIdToUse !== existingShopifySessionId
    ) {
      localStorage.setItem(
        ANONYMOUS_SHOPIFY_SESSION_ID_KEY,
        shopifySessionIdToUse,
      );
    }

    const shouldSyncCartAttributes =
      marketingEnabled ||
      (autocheck.conversion.enabled &&
        autocheck.conversion.predictiveConversion.enabled);

    if (shouldSyncCartAttributes) {
      try {
        const ajaxClient = ShopifyAjaxClient.fromEnv();
        const cart = await ajaxClient.cartGet();

        await Promise.all([
          (async () => {
            if (
              cart?.attributes?.[ANONYMOUS_ID_CART_ATTRIBUTE_KEY] !== idToUse
            ) {
              await ajaxClient.updateCartAttribute({
                attributes: [
                  { key: ANONYMOUS_ID_CART_ATTRIBUTE_KEY, value: idToUse },
                ],
              });
            }
          })(),
          (async () => {
            if (
              cart?.attributes?.[
                ANONYMOUS_SHOPIFY_SESSION_ID_CART_ATTRIBUTE_KEY
              ] !== shopifySessionIdToUse
            ) {
              if (shopifySessionIdToUse) {
                await ajaxClient.updateCartAttribute({
                  attributes: [
                    {
                      key: ANONYMOUS_SHOPIFY_SESSION_ID_CART_ATTRIBUTE_KEY,
                      value: shopifySessionIdToUse,
                    },
                  ],
                });
              } else {
                // remove the attribute
                await ajaxClient.updateCartAttribute({
                  attributes: [
                    {
                      key: ANONYMOUS_SHOPIFY_SESSION_ID_CART_ATTRIBUTE_KEY,
                      value: "",
                    },
                  ],
                });
              }
            }
          })(),
        ]);
      } catch (error) {
        log("Error syncing anonymous ID to cart attribute", error);
      }
    }
  }
}
void executeAndMonitor(async () =>
  ensureMarketingAnonymousIdPresent().catch(console.error),
);

function getCustomerEmailAndPhone() {
  const queryParameters = new URLSearchParams(location.search);
  const contactData = queryParameters.get("utm_contact");
  if (!contactData) return;

  const { email, phone } = contactDataFormat.read(decodeURI(contactData));

  if (email) {
    localStorage.setItem(CUSTOMER_EMAIL, email);
  }
  if (phone) {
    localStorage.setItem(CUSTOMER_PHONE, phone);
  }
}
void executeAndMonitor(async () => getCustomerEmailAndPhone());
